import { makeStyles } from "@material-ui/core/styles";
import Layout from "../Layouts/AppLayout";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { getOperatingHours, getSites, updateOpeningHours } from "../api";
import { useSnackbar } from "notistack";
import { Save } from "@material-ui/icons";
import { useCallback, useEffect, useState } from "react";
import { getFormPayload } from "../utils";
import { LocalizationProvider, TimePicker } from "@material-ui/lab";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  table: {
    marginTop: theme.spacing(4),
  },
  createButton: {
    marginLeft: "auto !important",
  },
  progress: {
    margin: "100px auto !important",
  },
  centerContainer: {
    margin: "0 auto",
  },
  labelTypes: {
    transform: "scale(0.8)",
    transformOrigin: "left",
    display: "flex",
    marginTop: 8,

    "& img": {
      marginLeft: "0 !important",
    },

    "& img[alt*='computeIT']": {
      position: "relative",
      top: 3,
    },
  },
}));

const DAYS = [
  ["Monday", "mon"],
  ["Tuesday", "tue"],
  ["Wednesday", "wed"],
  ["Thursday", "thu"],
  ["Friday", "fri"],
  ["Saturday", "sat"],
  ["Sunday", "sun"],
];

export default function OpeningHours() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [site, setSite] = useState("all");
  const [dirty, setDirty] = useState(false);

  const { data: sites } = useQuery(["sites"], getSites(), {
    onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
  });

  const { isLoading, data: operatingHours } = useQuery(
    ["operating-hours", site],
    getOperatingHours(site),
    {
      onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    }
  );

  const [tmpOperatingHours, setTmpOperatingHours] = useState({});
  useEffect(() => {
    let init = {};
    for (let [name, day] of DAYS) {
      init[day] = {
        open: moment()
          .startOf("day")
          .add(moment.duration(operatingHours?.[day]?.open ?? "00:00")),
        close: moment()
          .startOf("day")
          .add(moment.duration(operatingHours?.[day]?.close ?? "23:00")),
        closed: operatingHours?.[day]?.closed ?? false,
        fullDay: operatingHours?.[day]?.fullDay ?? false,
      };
    }
    setTmpOperatingHours(init);
  }, [operatingHours]);

  const { mutate: doSave, isLoading: isUpdateLoading } = useMutation(
    updateOpeningHours,
    {
      onSuccess: ({ message }) => {
        setDirty(false);
        queryClient.invalidateQueries(["operating-hours", site]);
        enqueueSnackbar(message, { variant: "success" });
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
    }
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      let settings = {};
      for (let [name, day] of DAYS) {
        settings[day] = {
          open: tmpOperatingHours[day].open.format("HH:mm"),
          close: tmpOperatingHours[day].close.format("HH:mm"),
          closed: tmpOperatingHours?.[day]?.closed ?? false,
          fullDay: tmpOperatingHours?.[day]?.fullDay ?? false,
        };
      }
      doSave({ siteId: site, settings });
    },
    [tmpOperatingHours, doSave, site]
  );

  const onTimeChange = useCallback(
    (day, attr) => (e) => {
      const tmp = { ...tmpOperatingHours };
      tmp[day][attr] = e;
      setTmpOperatingHours(tmp);
    },
    [tmpOperatingHours]
  );

  const onFieldChange = useCallback(
    (day, attr) => (e) => {
      const tmp = { ...tmpOperatingHours };
      tmp[day][attr] = !(tmp[day]?.[attr] ?? false);
      if (attr === "closed") tmp[day].fullDay = false;
      else if (attr === "fullDay") tmp[day].closed = false;
      setTmpOperatingHours(tmp);
    },
    [tmpOperatingHours]
  );

  const onSiteChange = (e) => {
    if (dirty) {
      const res = window.confirm(
        "You have unsaved changes, do you want to discard them?"
      );
      if (res) {
        setSite(e.target.value);
        setDirty(false);
      }
    } else {
      setSite(e.target.value);
      setDirty(false);
    }
  };

  return (
    <Layout>
      <Grid container spacing={3}>
        <Grid item xs={12} md={9} className={classes.centerContainer}>
          <form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Box display="flex">
              <Typography variant="h5">Opening Hours</Typography>
              <Button
                style={{ marginLeft: "auto" }}
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<Save />}
                disabled={isUpdateLoading}
              >
                Save
              </Button>
            </Box>

            <Box display="flex" mt={2} mb={2}>
              {sites && (
                <FormControl
                  sx={{ marginLeft: "auto", minWidth: 300 }}
                  size="small"
                >
                  <InputLabel>Selected site</InputLabel>
                  <Select
                    value={site}
                    onChange={onSiteChange}
                    label="Selected site"
                  >
                    <MenuItem value={`all`}>[Default] All sites</MenuItem>
                    {sites.map((e) => (
                      <MenuItem key={e.id} value={e.id}>
                        {e.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>

            {site === "all" ? (
              <Alert severity="info" style={{ marginBottom: 16 }}>
                This is the default configuration to set operating hours for all
                site locations, settings on individual sites will override this
                setting.
              </Alert>
            ) : (
              <Alert mt={4} severity="info" style={{ marginBottom: 16 }}>
                This configuration will set operating hours for this site
                location, it will override the default if set.
              </Alert>
            )}

            {isLoading ? (
              <Box display="flex">
                <CircularProgress className={classes.progress} />
              </Box>
            ) : (
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <TableContainer className={classes.table}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell align="left"></TableCell>
                        <TableCell align="left">Closed</TableCell>
                        <TableCell align="left">24 Hours</TableCell>
                        <TableCell align="left">Open</TableCell>
                        <TableCell align="left">Close</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {DAYS.map(([name, day]) => (
                        <TableRow key={`${site}-${day}`}>
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{ verticalAlign: "text-top", maxWidth: 100 }}
                          >
                            {name}
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{ verticalAlign: "text-top", maxWidth: 100 }}
                          >
                            <Checkbox
                              color="primary"
                              checked={
                                tmpOperatingHours?.[day]?.closed ?? false
                              }
                              onChange={onFieldChange(day, "closed")}
                            />
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{ verticalAlign: "text-top", maxWidth: 100 }}
                          >
                            <Checkbox
                              color="primary"
                              checked={
                                tmpOperatingHours?.[day]?.fullDay ?? false
                              }
                              onChange={onFieldChange(day, "fullDay")}
                            />
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ verticalAlign: "text-top", width: 180 }}
                          >
                            {!tmpOperatingHours?.[day]?.closed &&
                              !tmpOperatingHours?.[day]?.fullDay && (
                                <TimePicker
                                  label=""
                                  value={tmpOperatingHours?.[day]?.open ?? ""}
                                  onChange={onTimeChange(day, "open")}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      size="small"
                                      helperText=""
                                      style={{
                                        maxWidth: 180,
                                      }}
                                    />
                                  )}
                                />
                              )}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ verticalAlign: "text-top", width: 180 }}
                          >
                            {!tmpOperatingHours?.[day]?.closed &&
                              !tmpOperatingHours?.[day]?.fullDay && (
                                <TimePicker
                                  label=""
                                  value={tmpOperatingHours?.[day]?.close ?? ""}
                                  onChange={onTimeChange(day, "close")}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      size="small"
                                      helperText=""
                                      style={{
                                        maxWidth: 180,
                                      }}
                                    />
                                  )}
                                />
                              )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </LocalizationProvider>
            )}
          </form>
        </Grid>
      </Grid>
    </Layout>
  );
}
